import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Breadcrumbs from '@common/components/Breadcrumbs';
import { utcTimeToLocal, localTimeToUtc } from '@common/utils/dateUtils';
import { fetchSavedReports } from '@api/savedReports';
import { fetchRecipients } from '@api/recipients';
import { createScheduledReport, updateScheduledReport, fetchScheduledReportById } from '@api/scheduledReports';
import SaveScheduledReportModal from '../SaveScheduledReportModal/SaveScheduledReportModal';
import SelectReportsSection from './sections/SelectReportsSection';
import ExecutionScheduleSection from './sections/ExecutionScheduleSection';
import DeliveryRecipientsSection from './sections/DeliveryRecipientsSection';
import * as styles from './ScheduleReportPage.module.css';

/**
 * ScheduleReportPage Component
 *
 * Page for creating or editing a scheduled report
 * Matches Figma design: https://www.figma.com/design/DwhBVgd7QzzcwqWz8tzRnx/Reports?node-id=889-42758
 */
function ScheduleReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditMode = id && id !== 'new';

  // Section expansion state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    selectReports: true,
    executionSchedule: true,
    deliveryRecipients: true,
  });

  // Form state - Select Reports (from Saved Reports)
  const [selectedSavedReports, setSelectedSavedReports] = useState([]);

  // Form state - Execution Schedule
  const [frequency, setFrequency] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [selectedDays, setSelectedDays] = useState([]);

  // Form state - Delivery & Recipients
  const [shareVia, setShareVia] = useState({ email: true });
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [activeRecipientFilters, setActiveRecipientFilters] = useState(['contact', 'contactGroup']);

  // Saving state
  const [saving, setSaving] = useState(false);

  // Modal state for name/description/format
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Edit mode - store existing report data for the modal
  const [existingReportName, setExistingReportName] = useState('');
  const [existingReportDescription, setExistingReportDescription] = useState('');
  const [existingOutputFormat, setExistingOutputFormat] = useState('pdf');

  // Use react-query for data fetching
  const { data: savedReports = [], isLoading: loadingSavedReports } = useQuery({
    queryKey: ['savedReports'],
    queryFn: async () => {
      const result = await fetchSavedReports();
      if (result.status === 'success') {
        // Filter to only show enabled saved reports (status = 1)
        const allReports = result.data || [];
        return allReports.filter((report) => report.status === 1);
      }
      throw new Error(result.message || 'Failed to load saved reports');
    },
  });

  const { data: recipients = [], isLoading: loadingRecipients } = useQuery({
    queryKey: ['recipients'],
    queryFn: async () => {
      const result = await fetchRecipients();
      if (result.status === 'success') {
        return result.data || [];
      }
      throw new Error(result.message || 'Failed to load recipients');
    },
  });

  const { data: existingReport, isLoading: loadingReport } = useQuery({
    queryKey: ['scheduledReport', id],
    queryFn: async () => {
      // Check if report data is in location state first
      const stateReport = location.state?.scheduledReport;
      if (stateReport) {
        return stateReport;
      }
      // Otherwise fetch from API
      const result = await fetchScheduledReportById(id);
      if (result.status === 'success' && result.data) {
        return result.data;
      }
      throw new Error(result.message || 'Failed to load scheduled report');
    },
    enabled: isEditMode && savedReports.length > 0,
  });

  // Populate form when existing report is loaded (edit mode)
  useEffect(() => {
    if (existingReport && savedReports.length > 0) {
      // Populate modal fields (name, description, format)
      setExistingReportName(existingReport.name || '');
      setExistingReportDescription(existingReport.description || '');
      setExistingOutputFormat(existingReport.outputFormat || 'pdf');

      // Set selected saved reports from the scheduled report
      if (existingReport.savedReportIds && existingReport.savedReportIds.length > 0) {
        setSelectedSavedReports(existingReport.savedReportIds);
      }

      // Populate schedule fields
      setFrequency(existingReport.scheduleType || 'weekly');
      // scheduleTime is stored in UTC; convert to local for display
      setScheduleTime(utcTimeToLocal(existingReport.scheduleTime) || '09:00');
      if (existingReport.scheduleType === 'weekly' && existingReport.scheduleDays) {
        setSelectedDays(existingReport.scheduleDays.split(','));
      } else {
        setSelectedDays([]);
      }
      // Populate date fields
      if (existingReport.startDate) setStartDate(existingReport.startDate);
      if (existingReport.endDate) setEndDate(existingReport.endDate);

      // Populate recipients if available
      if (existingReport.recipients && Array.isArray(existingReport.recipients)) {
        setSelectedRecipients(existingReport.recipients);
      }
    }
  }, [existingReport, savedReports]);

  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Handle day selection
  const handleDayToggle = useCallback((dayValue) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayValue)) {
        return prev.filter((d) => d !== dayValue);
      }
      return [...prev, dayValue].sort((a, b) => Number(a) - Number(b));
    });
  }, []);

  // Handle filter toggle for recipients
  // MultiSelectList passes the entire new filters array
  const handleRecipientFilterToggle = useCallback((newFilters) => {
    // Ensure at least one filter is always active
    if (newFilters.length === 0) {
      return;
    }
    setActiveRecipientFilters(newFilters);
  }, []);

  // Handle actual save (used by both modal submission and direct save in edit mode)
  const handleSaveScheduledReport = useCallback(
    async ({ name, description, outputFormat }) => {
      setSaving(true);
      setSaveError(null);

      try {
        // Get the first selected saved report to use its dashboardId
        const firstSavedReport = savedReports.find((sr) => selectedSavedReports.includes(sr.id));

        if (!firstSavedReport) {
          throw new Error('Selected saved report not found');
        }

        const scheduleData = {
          name,
          description,
          savedReportIds: selectedSavedReports,
          outputFormat,
          scheduleType: frequency,
          scheduleTime: localTimeToUtc(scheduleTime),
          scheduleDays: frequency === 'weekly' && selectedDays.length > 0
            ? selectedDays.join(',')
            : null,
          startDate: startDate || null,
          endDate: endDate || null,
          recipients: selectedRecipients,
        };

        let result;
        if (isEditMode) {
          result = await updateScheduledReport(id, scheduleData);
        } else {
          result = await createScheduledReport(scheduleData);
        }

        if (result.status === 'success') {
          setShowSaveModal(false);
          navigate('/scheduled');
        } else {
          setSaveError(result.message || t('scheduledReports.errors.saveFailed'));
        }
      } catch {
        setSaveError(t('scheduledReports.errors.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [
      savedReports,
      selectedSavedReports,
      frequency,
      scheduleTime,
      selectedDays,
      startDate,
      endDate,
      selectedRecipients,
      isEditMode,
      id,
      navigate,
      t,
    ],
  );

  // Handle save button click
  const handleSaveClick = useCallback(() => {
    setSaveError(null);

    if (selectedSavedReports.length === 0) {
      setSaveError(t('scheduledReports.errors.selectReport'));
      return;
    }

    if (startDate) {
      const scheduledDateTime = new Date(`${startDate}T${scheduleTime || '09:00'}`);
      if (scheduledDateTime <= new Date()) {
        setSaveError(t('scheduledReports.errors.scheduleDateTimePast'));
        return;
      }
    }

    if (isEditMode) {
      // In edit mode, save directly with existing name/description/format
      handleSaveScheduledReport({
        name: existingReportName,
        description: existingReportDescription,
        outputFormat: existingOutputFormat,
      });
    } else {
      // In create mode, open modal
      setSaveError(null);
      setShowSaveModal(true);
    }
  }, [
    selectedSavedReports,
    startDate,
    scheduleTime,
    t,
    isEditMode,
    existingReportName,
    existingReportDescription,
    existingOutputFormat,
    handleSaveScheduledReport,
  ]);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowSaveModal(false);
    setSaveError(null);
  }, []);

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate('/scheduled');
  }, [navigate]);

  // Breadcrumb items
  const breadcrumbItems = useMemo(
    () => [
      { label: t('reports.pageTitle'), path: '/' },
      { label: t('scheduledReports.title'), path: '/scheduled' },
      {
        label: isEditMode
          ? t('scheduledReports.actions.modifyScheduledReport')
          : t('scheduledReports.addScheduledReport'),
      },
    ],
    [t, isEditMode],
  );

  // Loading state
  const isLoading = loadingSavedReports || (isEditMode && loadingReport);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Breadcrumbs items={breadcrumbItems} />
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Breadcrumbs items={breadcrumbItems} />

      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('scheduledReports.page.title')}</h1>
        <p className={styles.pageSubtitle}>{t('scheduledReports.page.subtitle')}</p>
      </div>

      {/* Form Content */}
      <div className={styles.formContent}>
        {/* Section 1: Select Reports */}
        <SelectReportsSection
          expanded={sectionsExpanded.selectReports}
          onToggle={() => toggleSection('selectReports')}
          savedReports={savedReports}
          selectedSavedReports={selectedSavedReports}
          onSelectionChange={setSelectedSavedReports}
          loading={loadingSavedReports}
        />

        {/* Section 2: Execution Schedule */}
        <ExecutionScheduleSection
          expanded={sectionsExpanded.executionSchedule}
          onToggle={() => toggleSection('executionSchedule')}
          frequency={frequency}
          onFrequencyChange={setFrequency}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          scheduleTime={scheduleTime}
          onScheduleTimeChange={setScheduleTime}
          selectedDays={selectedDays}
          onDayToggle={handleDayToggle}
        />

        {/* Section 3: Delivery & Recipients */}
        <DeliveryRecipientsSection
          expanded={sectionsExpanded.deliveryRecipients}
          onToggle={() => toggleSection('deliveryRecipients')}
          shareVia={shareVia}
          onShareViaChange={setShareVia}
          recipients={recipients}
          selectedRecipients={selectedRecipients}
          onSelectionChange={setSelectedRecipients}
          activeRecipientFilters={activeRecipientFilters}
          onFilterToggle={handleRecipientFilterToggle}
          loading={loadingRecipients}
        />
      </div>

      {/* Inline save error (visible in edit mode where the modal is not used) */}
      {saveError && !showSaveModal && (
        <div className={styles.saveError}>{saveError}</div>
      )}

      {/* Footer Actions */}
      <div className={styles.footer}>
        <button type="button" className={styles.cancelButton} onClick={handleCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSaveClick}
          disabled={saving || selectedSavedReports.length === 0}
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>

      {/* Save Modal for name, description, and format */}
      <SaveScheduledReportModal
        isOpen={showSaveModal}
        onClose={handleCloseModal}
        onSave={handleSaveScheduledReport}
        saving={saving}
        error={saveError}
        defaultName={existingReportName}
        defaultDescription={existingReportDescription}
        defaultOutputFormat={existingOutputFormat}
        isEditMode={isEditMode}
      />
    </div>
  );
}

export default ScheduleReportPage;
